// src/components/auth/login/Login.js
import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useMutation } from "@apollo/client";
import { useApolloClient } from "@apollo/client";
import {
  Backdrop,
  CircularProgress,
  OutlinedInput,
  Typography,
  Button,
  Link,
  Box,
  Grid,
  Paper,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { LOGIN_USER, SIGNUP_USER_GOOGLE } from "../../../graphql/mutations";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { normalizeBusinessData } from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames } from "../../../graphql/queries";
import { GET_MY_BUSINESSES } from "../../../graphql/queries";
import { useAuthStatus } from "../../../util/useAuthStatus";
import { useTokenLoader } from "../../../util/tokenLoader";
import { useDispatch } from "react-redux";
import { addBusinesses, setSelectedBusiness } from "../../../redux/actions/businessActions";
import Logo from "../../../assets/images/plany365.ico";
import { getSafeReturnTo, setToken } from "../../../util/authUtils";
import { isConciergeUser } from "../../../util/conciergeAccess";
import {
  hasCareWorkspaceAccess,
  hasStandaloneBookingAccess,
} from "../../../../core/business/productAccess";

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function normalizeAgentRedirect(path, isAgent) {
  if (!isAgent) return path;
  const normalized = typeof path === "string" ? path : "";
  if (!normalized) return "/care/agent";
  if (normalized === "/care" || normalized.startsWith("/care?")) {
    return "/care/agent";
  }
  return normalized;
}

export default function Login({ login: onLoginSuccess }) {
  const apolloClient = useApolloClient();
  const dispatch = useDispatch();
  const { trigger: fetchToken } = useTokenLoader();
  const { isLoggedIn } = useAuthStatus();
  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );

  const location = useLocation();
  const navigate = useNavigate();
  const brandName = "MicroLogic";

  // Read and sanitize the returnTo target (prevents open-redirects)
  const rawReturnTo = new URLSearchParams(location.search).get("returnTo");
  const safeReturnTo = getSafeReturnTo(rawReturnTo);
  const storedFrom = getSafeReturnTo(localStorage.getItem("redirectAfterLogin"));
  const brandHref = "/";
  const handleBrandClick = (e) => {
    e.preventDefault();
    navigate("/", { replace: true });
  };

  // Pre-fill email from query if present
  const [email, setEmail] = useState(
    new URLSearchParams(window.location.search).get("email") || ""
  );
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState([]);
  const [updating, setUpdating] = useState(false);

  // GraphQL mutations
  const [loginUser] = useMutation(LOGIN_USER);
  const [signUpWithGoogle] = useMutation(SIGNUP_USER_GOOGLE);

  const hydrateBusinessesAfterLogin = async (decoded) => {
    const defaultBusinessId = Number(decoded?.defaultBusinessId || 0);
    if (!defaultBusinessId) return;

    const result = await apolloClient.query({
      query: GET_MY_BUSINESSES,
      fetchPolicy: "network-only",
    });
    const businesses = normalizeBusinessData(result?.data?.myBusinesses || []);
    if (businesses.length) {
      dispatch(addBusinesses(businesses, "MY_BUSINESSES"));
      const preferredBusinessId = businesses.some(
        (business) =>
          Number(business?.businessBasic?.id) === Number(defaultBusinessId)
      )
        ? defaultBusinessId
        : Number(businesses[0]?.businessBasic?.id || 0);
      if (preferredBusinessId) {
        dispatch(setSelectedBusiness(preferredBusinessId));
      }
    }
  };

  // Decide where to go after successful login
  function determineDestination(decoded) {
    const isAgent = isConciergeUser(decoded || { email });
    // 1) Highest priority: explicit returnTo from query string
    if (safeReturnTo) return normalizeAgentRedirect(safeReturnTo, isAgent);

    // 2) If you guarded routes via <Navigate state={{ from: ... }}>,
    //    honor that as a next fallback:
    const fromState = location.state && location.state.from;
    if (fromState) return normalizeAgentRedirect(fromState, isAgent);

    if (storedFrom) return normalizeAgentRedirect(storedFrom, isAgent);

    if (isAgent) {
      return "/care/agent";
    }

    // 3) Otherwise compute your default route
    const canUseStandaloneBooking = hasStandaloneBookingAccess(decoded);
    const canUseCareWorkspace = hasCareWorkspaceAccess(decoded);
    const url = canUseStandaloneBooking
      ? "/biz/bookingtypes?v=booking"
      : canUseCareWorkspace
        ? "/biz/dashboard"
        : "/biz/business";

    return decoded.defaultBusinessId
      ? url
      : canUseStandaloneBooking
        ? "/booking"
        : "/care";
  }

  const completeGoogleLogin = async ({ email: googleEmail, name }) => {
    const res = await signUpWithGoogle({
      variables: {
        email: googleEmail,
        username: name || googleEmail,
      },
    });

    const token = res.data.signUpGoogle.token;
    setToken(token);
    onLoginSuccess(token);

    const decoded = jwtDecode(token);
    await fetchToken();

    if (decoded.defaultBusinessId) {
      await hydrateBusinessesAfterLogin(decoded);
      await reloadBusinesses();
    }

    const dest = determineDestination(decoded);
    navigate(dest, { replace: true });
  };

  const handleGoogleCredentialSuccess = async (credentialResponse) => {
    setUpdating(true);
    setErrors([]);
    try {
      const credential = credentialResponse?.credential;
      if (!credential) throw new Error("Missing Google credential.");
      const profile = jwtDecode(credential);
      if (!profile?.email) throw new Error("Google did not return an email.");
      await completeGoogleLogin({ email: profile.email, name: profile.name });
    } catch (err) {
      console.error("Google login failed:", err);
      setErrors(["Something went wrong with Google login."]);
    } finally {
      setUpdating(false);
    }
  };

  // Handle email/password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    if (!email.trim()) {
      setErrors(["Email is required"]);
      return;
    }
    if (!password.trim()) {
      setErrors(["Password is required"]);
      return;
    }

    try {
      const result = await loginUser({ variables: { email, password } });
      const token = result.data.login.token;

      // Persist and bootstrap auth
      setToken(token);
      onLoginSuccess(token);

      const decoded = jwtDecode(token);
      await fetchToken();

      if (decoded.defaultBusinessId) {
        await hydrateBusinessesAfterLogin(decoded);
        await reloadBusinesses();
      }

      // Redirect
      const dest = determineDestination(decoded);
      navigate(dest, { replace: true });
    } catch (err) {
      setErrors([err.message || "Login failed. Please try again."]);
    }
  };

  // Links that preserve returnTo (optional but nice UX)
  const signupHref = `/signup${safeReturnTo ? `?returnTo=${encodeURIComponent(safeReturnTo)}` : ""}`;
  const forgotHref = `/forgotpassword${safeReturnTo ? `?returnTo=${encodeURIComponent(safeReturnTo)}` : ""}`;


  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 100px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 3, sm: 4 },
        backgroundColor: "#ffffff",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 980,
          borderRadius: 4,
          border: "1px solid #dbe4ee",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.12)",
          backgroundColor: "#ffffff",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: 5,
            background:
              "linear-gradient(160deg, #0b1f33 0%, #15314c 58%, #1b4464 100%)",
            color: "#ffffff",
          }}
        >
          <Box
            component={RouterLink}
            to={brandHref}
            onClick={handleBrandClick}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "inherit", textDecoration: "none" }}
          >
            <Box
              component="img"
              src={Logo}
              alt={brandName}
              sx={{ width: 42, height: 42, borderRadius: 1, backgroundColor: "#fff" }}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.4 }}>
              {brandName}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 36, lineHeight: 1.1, fontWeight: 800, mb: 1.5 }}>
              One platform, focused workspaces.
            </Typography>
            <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)", maxWidth: 360 }}>
              Sign in to the workspace your business uses across Care, Booking, and related tools, with each experience tailored to the way your team works.
            </Typography>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.22)",
              borderRadius: 2,
              p: 2,
              backgroundColor: "rgba(255,255,255,0.10)",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.95)" }}>
              Professional tools for modern businesses
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Typography
            component={RouterLink}
            to={brandHref}
            onClick={handleBrandClick}
            sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, color: "#3b82f6", mb: 1, textDecoration: "none", display: "inline-block" }}
          >
            {brandName}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>
            Welcome back
          </Typography>
          <Typography variant="body2" sx={{ color: "#475569", mb: 3 }}>
            Sign in to continue into your workspace.
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.75 }}>Email</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "#fff" }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.75 }}>Password</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Enter password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "#fff" }}
                />
              </Grid>
            </Grid>

            <Box mt={1.5} textAlign="right">
              <Link href={forgotHref} underline="hover" sx={{ fontWeight: 600, color: "#2563eb" }}>
                Forgot your password?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 2.5,
                py: 1.25,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                background: "linear-gradient(135deg, #1e40af 0%, #0369a1 100%)",
              }}
            >
              Login
            </Button>
          </form>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2.25 }}>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "#dbe4ee" }} />
            <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>OR</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "#dbe4ee" }} />
          </Box>

          <Box
            sx={{
              width: "100%",
              "& > div": { width: "100% !important" },
              "& iframe": { width: "100% !important" },
            }}
          >
            {googleClientId ? (
              <GoogleLogin
                onSuccess={handleGoogleCredentialSuccess}
                onError={() => setErrors(["Google login failed. Try again."])}
                text="continue_with"
                shape="rectangular"
                size="large"
                width="100%"
                useOneTap={false}
              />
            ) : (
              <Typography
                sx={{
                  color: "#92400e",
                  fontSize: 13,
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                }}
              >
                Google login is temporarily unavailable. Please use email and password to sign in.
              </Typography>
            )}
          </Box>
          {errors.length > 0 && (
            <Box mt={2}>
              {errors.map((msg, i) => (
                <Typography
                  key={i}
                  sx={{
                    mb: 0.5,
                    color: "#b91c1c",
                    fontSize: 13,
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  {msg}
                </Typography>
              ))}
            </Box>
          )}

          <Typography align="center" variant="body2" sx={{ mt: 2.5, color: "#475569" }}>
            Don’t have an account? <Link href={signupHref} sx={{ fontWeight: 700 }}>Sign Up</Link>
          </Typography>

          <Backdrop open={updating} sx={{ zIndex: 9999, color: "#fff" }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Signing you in...
            </Typography>
          </Backdrop>
        </Box>
      </Paper>
    </Box>
  );
}
